import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Share2, Film, Plane, Clapperboard, Globe, Camera, PenTool, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../../lib/i18n";
import { useServices, useProjects } from "../../lib/queries";
import { optimizeImg } from "../../lib/img";

const ICONS = { Share2, Film, Plane, Clapperboard, Globe, Camera, PenTool, Sparkles };

export default function Services() {
  const { data: services = [] } = useServices();
  const { data: projects = [] } = useProjects();
  const { t, pick } = useLang();
  const navigate = useNavigate();

  const projectCount = (slug) =>
    projects.filter((p) => p.category === slug || p.services?.includes(slug)).length;

  const spanClass = (span, idx) => {
    // Bento layout by index for variety
    const patterns = [
      "md:col-span-4 md:row-span-2", // 0 tall
      "md:col-span-4",                // 1
      "md:col-span-4",                // 2
      "md:col-span-4",                // 3
      "md:col-span-8",                // 4 wide
      "md:col-span-4",                // 5
      "md:col-span-4",                // 6
      "md:col-span-4",                // 7
    ];
    if (span === "wide") return "md:col-span-8";
    if (span === "tall") return "md:col-span-4 md:row-span-2";
    return patterns[idx % patterns.length];
  };

  return (
    <section id="services" data-testid="services-section" className="py-32 relative">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-[#A855F7]" />
              <span className="font-mono-ui text-[10px] tracking-[0.3em] text-[#A855F7] uppercase">
                {t("services_kicker")}
              </span>
            </div>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase tracking-tighter text-white max-w-4xl">
              {t("services_title")}
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-base md:text-lg">{t("services_desc")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[220px] gap-4">
          {services.map((s, i) => {
            const Icon = ICONS[s.icon] || Sparkles;
            const isTall = s.span === "tall" || i === 0;
            return (
              <motion.button
                key={s.id}
                data-testid={`service-card-${s.slug}`}
                onClick={() => navigate(`/services/${s.slug}`)}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.05, ease: [0.2, 0.9, 0.2, 1] }}
                className={`group relative overflow-hidden rounded-2xl border border-white/5 bg-[#101018] text-left transition-colors duration-300 hover:border-[var(--accent-bright,#A855F7)]/50 ${spanClass(s.span, i)} min-h-[220px] contain-paint`}
                style={{ gridRow: isTall ? "span 2" : undefined }}
              >
                <img
                  src={optimizeImg(s.cover_image, 900, 72)}
                  alt={s.title_tr}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-70 transition-opacity duration-300 pointer-events-none"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#08080D] via-[#08080D]/70 to-transparent pointer-events-none" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[radial-gradient(circle_at_50%_100%,rgba(168,85,247,0.22),transparent_60%)] pointer-events-none" />

                <div className="relative h-full p-6 md:p-8 flex flex-col justify-between">
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center bg-black/40 backdrop-blur-md text-white/90 group-hover:border-[#A855F7]/60 group-hover:text-[#A855F7] transition-colors">
                      <Icon size={16} />
                    </div>
                    <span className="font-mono-ui text-[10px] tracking-widest text-white/50 uppercase">
                      {projectCount(s.slug).toString().padStart(2, "0")} · works
                    </span>
                  </div>

                  <div>
                    <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight text-white leading-tight">
                      {pick(s, "title")}
                    </h3>
                    <p className="mt-3 text-sm text-white/60 max-w-sm">
                      {pick(s, "description")}
                    </p>
                    <div className="mt-5 flex items-center gap-2 text-white/70 group-hover:text-[#A855F7] transition-colors">
                      <span className="text-xs uppercase tracking-widest font-medium">
                        {t("services_more")}
                      </span>
                      <ArrowUpRight size={14} className="transition-transform group-hover:rotate-45" />
                    </div>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
